/**
 * Reports & Analytics Page
 * Business insights and financial reports with generation capabilities
 */

import React, { useState, useEffect } from 'react';
import { useAuthStore } from '../store/authStore';
import { useInvoiceStore } from '../store/invoiceStore';
import { useClientStore } from '../store/clientStore';
import { getExpenses } from '../services/expenseService';
import { getPayments } from '../services/paymentService';
import { formatCurrency, formatDate } from '../utils/formatters';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import './Reports.css';

const Reports = () => {
  const user = useAuthStore((state) => state.user);
  const invoices = useInvoiceStore((state) => state.invoices);
  const clients = useClientStore((state) => state.clients);
  const [expenses, setExpenses] = useState([]);
  const [payments, setPayments] = useState([]);
  const [dateRange, setDateRange] = useState('month'); // month, quarter, year, all
  const [loading, setLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState('');
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [user]);

  const loadData = async () => {
    try {
      const [expenseData, paymentData] = await Promise.all([
        getExpenses(user.uid),
        getPayments(user.uid),
      ]);
      setExpenses(expenseData);
      setPayments(paymentData);
      setLoading(false);
    } catch (error) {
      console.error('Error loading data:', error);
      setLoading(false);
    }
  };

  // Filter data by date range
  const getFilteredData = () => {
    const now = new Date();
    let startDate = new Date();

    switch (dateRange) {
      case 'month':
        startDate.setMonth(now.getMonth() - 1);
        break;
      case 'quarter':
        startDate.setMonth(now.getMonth() - 3);
        break;
      case 'year':
        startDate.setFullYear(now.getFullYear() - 1);
        break;
      default:
        startDate = new Date(0); // All time
    }

    return {
      invoices: invoices.filter(inv => {
        const invDate = inv.issueDate?.toDate ? inv.issueDate.toDate() : new Date(inv.issueDate);
        return invDate >= startDate;
      }),
      expenses: expenses.filter(exp => {
        const expDate = exp.date?.toDate ? exp.date.toDate() : new Date(exp.date);
        return expDate >= startDate;
      }),
      payments: payments.filter(pay => {
        const payDate = pay.paymentDate?.toDate ? pay.paymentDate.toDate() : new Date(pay.paymentDate);
        return payDate >= startDate;
      }),
    };
  };

  const { invoices: filteredInvoices, expenses: filteredExpenses, payments: filteredPayments } = getFilteredData();

  // Calculate key metrics
  const totalRevenue = filteredInvoices
    .filter(inv => inv.status === 'paid')
    .reduce((sum, inv) => sum + inv.totalAmount, 0);

  const pendingRevenue = filteredInvoices
    .filter(inv => inv.status === 'pending')
    .reduce((sum, inv) => sum + inv.totalAmount, 0);

  const totalExpenses = filteredExpenses.reduce((sum, exp) => sum + exp.amount, 0);
  const profit = totalRevenue - totalExpenses;
  const profitMargin = totalRevenue > 0 ? ((profit / totalRevenue) * 100).toFixed(1) : 0;

  const totalCollections = filteredPayments.reduce((sum, pay) => sum + pay.amount, 0);

  // Report Generation Functions
  const generateSalesReport = () => {
    const doc = new jsPDF();
    const dateRangeLabel = dateRange === 'all' ? 'All Time' : `Last ${dateRange.charAt(0).toUpperCase() + dateRange.slice(1)}`;

    doc.setFontSize(20);
    doc.text('Sales Report', 14, 22);
    doc.setFontSize(10);
    doc.text(`Period: ${dateRangeLabel}`, 14, 32);
    doc.text(`Generated: ${new Date().toLocaleDateString()}`, 14, 38);

    // Summary
    doc.setFontSize(14);
    doc.text('Summary', 14, 52);
    doc.setFontSize(10);
    doc.text(`Total Sales: ${formatCurrency(totalRevenue + pendingRevenue)}`, 14, 62);
    doc.text(`Paid: ${formatCurrency(totalRevenue)}`, 14, 68);
    doc.text(`Pending: ${formatCurrency(pendingRevenue)}`, 14, 74);
    doc.text(`Total Invoices: ${filteredInvoices.length}`, 14, 80);

    // Invoice Details Table
    const tableData = filteredInvoices.map(inv => [
      inv.invoiceNumber,
      inv.clientName,
      formatDate(inv.issueDate),
      formatCurrency(inv.totalAmount),
      inv.status.charAt(0).toUpperCase() + inv.status.slice(1),
    ]);

    doc.autoTable({
      startY: 90,
      head: [['Invoice #', 'Client', 'Date', 'Amount', 'Status']],
      body: tableData,
      theme: 'striped',
      headStyles: { fillColor: [41, 98, 255] },
    });

    doc.save(`sales-report-${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const generateCollectionReport = () => {
    const doc = new jsPDF();
    const dateRangeLabel = dateRange === 'all' ? 'All Time' : `Last ${dateRange.charAt(0).toUpperCase() + dateRange.slice(1)}`;

    doc.setFontSize(20);
    doc.text('Collection Report', 14, 22);
    doc.setFontSize(10);
    doc.text(`Period: ${dateRangeLabel}`, 14, 32);
    doc.text(`Generated: ${new Date().toLocaleDateString()}`, 14, 38);

    // Summary
    doc.setFontSize(14);
    doc.text('Summary', 14, 52);
    doc.setFontSize(10);
    doc.text(`Total Collections: ${formatCurrency(totalCollections)}`, 14, 62);
    doc.text(`Number of Payments: ${filteredPayments.length}`, 14, 68);
    doc.text(`Outstanding Balance: ${formatCurrency(pendingRevenue)}`, 14, 74);

    // Payment Details Table
    const tableData = filteredPayments.map(pay => [
      pay.invoiceNumber || 'N/A',
      pay.clientName,
      formatDate(pay.paymentDate),
      formatCurrency(pay.amount),
      pay.paymentMethod || 'N/A',
    ]);

    doc.autoTable({
      startY: 84,
      head: [['Invoice #', 'Client', 'Payment Date', 'Amount', 'Method']],
      body: tableData,
      theme: 'striped',
      headStyles: { fillColor: [16, 185, 129] },
    });

    doc.save(`collection-report-${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const generateProfitAndLoss = () => {
    const doc = new jsPDF();
    const dateRangeLabel = dateRange === 'all' ? 'All Time' : `Last ${dateRange.charAt(0).toUpperCase() + dateRange.slice(1)}`;

    doc.setFontSize(20);
    doc.text('Profit and Loss Statement', 14, 22);
    doc.setFontSize(10);
    doc.text(`Period: ${dateRangeLabel}`, 14, 32);
    doc.text(`Generated: ${new Date().toLocaleDateString()}`, 14, 38);

    // Revenue Section
    doc.setFontSize(14);
    doc.text('Revenue', 14, 55);
    doc.setFontSize(10);
    doc.text(`Sales Revenue: ${formatCurrency(totalRevenue)}`, 20, 65);
    doc.setFontSize(12);
    doc.text(`Total Revenue: ${formatCurrency(totalRevenue)}`, 14, 78);

    // Expenses Section
    doc.setFontSize(14);
    doc.text('Expenses', 14, 95);
    doc.setFontSize(10);

    // Group expenses by category
    const expensesByCategory = {};
    filteredExpenses.forEach(exp => {
      expensesByCategory[exp.category] = (expensesByCategory[exp.category] || 0) + exp.amount;
    });

    let yPos = 105;
    Object.entries(expensesByCategory).forEach(([category, amount]) => {
      doc.text(`${category}: ${formatCurrency(amount)}`, 20, yPos);
      yPos += 6;
    });

    doc.setFontSize(12);
    doc.text(`Total Expenses: ${formatCurrency(totalExpenses)}`, 14, yPos + 10);

    // Net Profit
    doc.setFontSize(16);
    doc.setTextColor(profit >= 0 ? 16 : 239, profit >= 0 ? 185 : 68, profit >= 0 ? 129 : 68);
    doc.text(`Net ${profit >= 0 ? 'Profit' : 'Loss'}: ${formatCurrency(Math.abs(profit))}`, 14, yPos + 30);
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    doc.text(`Profit Margin: ${profitMargin}%`, 14, yPos + 40);

    doc.save(`profit-loss-${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const generateBalanceSheet = () => {
    const doc = new jsPDF();

    doc.setFontSize(20);
    doc.text('Balance Sheet', 14, 22);
    doc.setFontSize(10);
    doc.text(`As of: ${new Date().toLocaleDateString()}`, 14, 32);

    // Assets Section
    doc.setFontSize(14);
    doc.text('Assets', 14, 50);
    doc.setFontSize(10);
    doc.text(`Accounts Receivable (Pending Invoices): ${formatCurrency(pendingRevenue)}`, 20, 60);
    doc.text(`Cash (Collected): ${formatCurrency(totalCollections)}`, 20, 66);
    doc.setFontSize(12);
    doc.text(`Total Assets: ${formatCurrency(pendingRevenue + totalCollections)}`, 14, 80);

    // Liabilities Section
    doc.setFontSize(14);
    doc.text('Liabilities', 14, 100);
    doc.setFontSize(10);
    doc.text(`Accounts Payable: ${formatCurrency(0)}`, 20, 110);
    doc.setFontSize(12);
    doc.text(`Total Liabilities: ${formatCurrency(0)}`, 14, 124);

    // Equity Section
    doc.setFontSize(14);
    doc.text('Equity', 14, 144);
    doc.setFontSize(10);
    doc.text(`Retained Earnings: ${formatCurrency(profit)}`, 20, 154);
    doc.setFontSize(12);
    doc.text(`Total Equity: ${formatCurrency(profit)}`, 14, 168);

    // Total
    doc.setFontSize(14);
    doc.text(`Total Liabilities + Equity: ${formatCurrency(profit)}`, 14, 188);

    doc.save(`balance-sheet-${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const handleGenerateReport = () => {
    if (!selectedReport) {
      alert('Please select a report type');
      return;
    }

    setGenerating(true);

    try {
      switch (selectedReport) {
        case 'sales':
          generateSalesReport();
          break;
        case 'collection':
          generateCollectionReport();
          break;
        case 'pnl':
          generateProfitAndLoss();
          break;
        case 'balance':
          generateBalanceSheet();
          break;
        default:
          alert('Unknown report type');
      }
    } catch (error) {
      console.error('Error generating report:', error);
      alert('Error generating report: ' + error.message);
    } finally {
      setGenerating(false);
    }
  };

  // Revenue by month
  const revenueByMonth = {};
  filteredInvoices
    .filter(inv => inv.status === 'paid')
    .forEach(inv => {
      const date = inv.paidAt?.toDate ? inv.paidAt.toDate() : inv.issueDate?.toDate ? inv.issueDate.toDate() : new Date(inv.issueDate);
      const month = date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
      revenueByMonth[month] = (revenueByMonth[month] || 0) + inv.totalAmount;
    });

  const expensesByMonth = {};
  filteredExpenses.forEach(exp => {
    const date = exp.date?.toDate ? exp.date.toDate() : new Date(exp.date);
    const month = date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
    expensesByMonth[month] = (expensesByMonth[month] || 0) + exp.amount;
  });

  // Combine for chart
  const monthlyData = Object.keys({ ...revenueByMonth, ...expensesByMonth })
    .sort((a, b) => new Date(a) - new Date(b))
    .map(month => ({
      month,
      revenue: revenueByMonth[month] || 0,
      expenses: expensesByMonth[month] || 0,
      profit: (revenueByMonth[month] || 0) - (expensesByMonth[month] || 0),
    }));

  // Revenue by client
  const revenueByClient = {};
  filteredInvoices
    .filter(inv => inv.status === 'paid')
    .forEach(inv => {
      revenueByClient[inv.clientName] = (revenueByClient[inv.clientName] || 0) + inv.totalAmount;
    });

  const topClients = Object.entries(revenueByClient)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([name, value]) => ({ name, value }));

  // Expenses by category
  const expensesByCategory = {};
  filteredExpenses.forEach(exp => {
    expensesByCategory[exp.category] = (expensesByCategory[exp.category] || 0) + exp.amount;
  });

  const expenseCategoryData = Object.entries(expensesByCategory)
    .sort((a, b) => b[1] - a[1])
    .map(([name, value]) => ({ name, value }));

  const COLORS = ['#2962ff', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#14b8a6'];

  if (loading) {
    return <div className="loading-state">Loading reports...</div>;
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Reports & Analytics</h1>
          <p className="page-subtitle">Business insights and financial reports</p>
        </div>
        <div className="header-actions">
          <select
            className="input"
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
          >
            <option value="month">Last Month</option>
            <option value="quarter">Last Quarter</option>
            <option value="year">Last Year</option>
            <option value="all">All Time</option>
          </select>
        </div>
      </div>

      {/* Report Generation Section */}
      <div className="report-generator card">
        <h3>Generate Reports</h3>
        <div className="report-generator-controls">
          <select
            className="input report-select"
            value={selectedReport}
            onChange={(e) => setSelectedReport(e.target.value)}
          >
            <option value="">Select Report Type...</option>
            <option value="sales">Sales Report</option>
            <option value="collection">Collection Report</option>
            <option value="pnl">Profit and Loss</option>
            <option value="balance">Balance Sheet</option>
          </select>
          <button
            className="btn btn-primary"
            onClick={handleGenerateReport}
            disabled={!selectedReport || generating}
          >
            {generating ? 'Generating...' : 'Generate PDF'}
          </button>
        </div>
        <p className="report-hint">
          Select a report type and click Generate to download a PDF report based on the selected date range.
        </p>
      </div>

      {/* Key Metrics */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon" style={{ backgroundColor: '#d1fae5' }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2" width="24" height="24">
              <line x1="12" y1="1" x2="12" y2="23" />
              <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
            </svg>
          </div>
          <div className="stat-content">
            <div className="stat-label">Total Revenue</div>
            <div className="stat-value">{formatCurrency(totalRevenue)}</div>
            <div className="stat-meta">Paid invoices</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{ backgroundColor: '#fee2e2' }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" width="24" height="24">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
          </div>
          <div className="stat-content">
            <div className="stat-label">Total Expenses</div>
            <div className="stat-value">{formatCurrency(totalExpenses)}</div>
            <div className="stat-meta">{filteredExpenses.length} expenses</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{ backgroundColor: profit >= 0 ? '#dbeafe' : '#fee2e2' }}>
            <svg viewBox="0 0 24 24" fill="none" stroke={profit >= 0 ? '#3b82f6' : '#ef4444'} strokeWidth="2" width="24" height="24">
              <polyline points={profit >= 0 ? "23 6 13.5 15.5 8.5 10.5 1 18" : "23 18 13.5 8.5 8.5 13.5 1 6"} />
              <polyline points={profit >= 0 ? "17 6 23 6 23 12" : "17 18 23 18 23 12"} />
            </svg>
          </div>
          <div className="stat-content">
            <div className="stat-label">Net Profit</div>
            <div className="stat-value" style={{ color: profit >= 0 ? '#10b981' : '#ef4444' }}>
              {formatCurrency(profit)}
            </div>
            <div className="stat-meta">{profitMargin}% margin</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{ backgroundColor: '#fef3c7' }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" width="24" height="24">
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
          </div>
          <div className="stat-content">
            <div className="stat-label">Pending Revenue</div>
            <div className="stat-value">{formatCurrency(pendingRevenue)}</div>
            <div className="stat-meta">Outstanding invoices</div>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="charts-grid">
        {/* Revenue vs Expenses Over Time */}
        <div className="chart-card">
          <h3>Revenue vs Expenses</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={monthlyData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip formatter={(value) => formatCurrency(value)} />
              <Legend />
              <Line type="monotone" dataKey="revenue" stroke="#10b981" strokeWidth={2} name="Revenue" />
              <Line type="monotone" dataKey="expenses" stroke="#ef4444" strokeWidth={2} name="Expenses" />
              <Line type="monotone" dataKey="profit" stroke="#2962ff" strokeWidth={2} name="Profit" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Top Clients */}
        <div className="chart-card">
          <h3>Top 5 Clients by Revenue</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={topClients}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip formatter={(value) => formatCurrency(value)} />
              <Bar dataKey="value" fill="#2962ff" name="Revenue" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Expenses by Category */}
        {expenseCategoryData.length > 0 && (
          <div className="chart-card">
            <h3>Expenses by Category</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={expenseCategoryData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {expenseCategoryData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => formatCurrency(value)} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Invoice Status Breakdown */}
        <div className="chart-card">
          <h3>Invoice Status</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={[
                  { name: 'Paid', value: filteredInvoices.filter(i => i.status === 'paid').length },
                  { name: 'Pending', value: filteredInvoices.filter(i => i.status === 'pending').length },
                  { name: 'Draft', value: filteredInvoices.filter(i => i.status === 'draft').length },
                ].filter(d => d.value > 0)}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                <Cell fill="#10b981" />
                <Cell fill="#f59e0b" />
                <Cell fill="#6b7280" />
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Summary Tables */}
      <div className="reports-tables">
        <div className="card">
          <h3>Client Summary</h3>
          <table className="table">
            <thead>
              <tr>
                <th>Client</th>
                <th>Invoices</th>
                <th>Revenue</th>
                <th>Pending</th>
              </tr>
            </thead>
            <tbody>
              {clients.map(client => {
                const clientInvoices = filteredInvoices.filter(inv => inv.clientId === client.id);
                const paid = clientInvoices.filter(inv => inv.status === 'paid').reduce((sum, inv) => sum + inv.totalAmount, 0);
                const pending = clientInvoices.filter(inv => inv.status === 'pending').reduce((sum, inv) => sum + inv.totalAmount, 0);

                if (clientInvoices.length === 0) return null;

                return (
                  <tr key={client.id}>
                    <td>{client.name}</td>
                    <td>{clientInvoices.length}</td>
                    <td style={{ fontWeight: '600' }}>{formatCurrency(paid)}</td>
                    <td style={{ color: pending > 0 ? '#f59e0b' : '#6b7280' }}>
                      {formatCurrency(pending)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Reports;
